
import { defineComponent, inject } from 'vue'
import Checkbox from '@/components/shared/Checkbox.vue'
import TextInput from '@/components/shared/TextInput.vue'
import LoadingPulse from '@/components/shared/LoadingPulse.vue'
import Paginator from '@/components/shared/Paginator.vue'
import { PROVIDER_CONSTANTS } from '@/constants/provider.constants'
import Toaster from '@meforma/vue-toaster'
import TrashCanSvg from '@/components/svgs/TrashCanSvg.vue'
import Dropdown from '@/components/shared/Dropdown.vue'
import { BuildTypeService } from '@/services/buildType.service'
import CustomerSearchMachine from '@/components/elements/CustomerSearchMachine.vue'
import { Order } from '@/@types/responses/order/Order'
import { OrderService } from '@/services/order.service'
import { Customer } from '@/@types/responses/customer/Customer'
import CartridgeSearchMachine from '@/components/elements/CartridgeSearchMachine.vue'
import { BuildTypeDropdownOption } from '@/@types/responses/build_type/BuildTypeDropdownOption'
import { ErrorsResponse } from '@/@types/responses/common/ErrorsResponse'

export default defineComponent({
  name: 'Orders',
  components: {
    CartridgeSearchMachine,
    Checkbox,
    CustomerSearchMachine,
    Dropdown,
    LoadingPulse,
    Paginator,
    TextInput,
    TrashCanSvg
  },
  setup() {
    const buildTypeService = inject(PROVIDER_CONSTANTS.buildTypeService) as BuildTypeService
    const orderService = inject(PROVIDER_CONSTANTS.orderService) as OrderService
    const toast = inject(PROVIDER_CONSTANTS.toaster) as Toaster

    return {
      buildTypeService,
      orderService,
      toast
    }
  },
  data() {
    let buildTypeId: number|undefined
    let cartridgeId: number|undefined
    let customer: Customer|undefined

    return {
      ascending: true,
      buildTypeDropdownOptions: [] as BuildTypeDropdownOption[],
      buildTypeId,
      cartridgeId,
      customer,
      filterOptionsExpanded: true,
      isScheduled: false,
      loading: true,
      onHold: false,
      orders: [] as Order[],
      pageNumber: 1,
      pageSize: 10,
      searchTerm: '',
      showCompletedOrders: false,
      sortField: 'Id',
      totalResults: 0
    }
  },
  computed: {
    customerName(): string {
      return this.customer ? `${this.customer.firstName} ${this.customer.lastName}` : ''
    }
  },
  mounted() {
    window.addEventListener("popstate", this.parseQueryStringAndSearch)

    this.fetchBuildTypeDropdownOptions().then(() => {
      this.parseQueryStringAndSearch()
    })
  },
  unmounted() {
    window.removeEventListener('popstate', this.parseQueryStringAndSearch)
  },
  methods: {
    booleanToString(value: boolean): string {
      return value ? 'Yes' : 'No'
    },
    clearSearchForm(): void {
      this.buildTypeId = undefined
      this.cartridgeId = undefined
      this.customer = undefined
      this.isScheduled = false
      this.onHold = false
      this.searchTerm = ''
      this.search()
    },
    fetchBuildTypeDropdownOptions(): Promise<void> {
      return this.buildTypeService.getBuildTypeDropdownOptions().then(dropdownOptionsResponse => {
        this.buildTypeDropdownOptions = dropdownOptionsResponse.dropdownOptions
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to load the build types. Try reloading the page.')
        }
      })
    },
    getBooleanParameterByName(name: string): boolean | undefined {
      const parameter = this.getStringParameterByName(name)
      return parameter !== undefined ? parameter === 'true' : undefined
    },
    getNumericParameterByName(name: string): number | undefined {
      const parameter = this.getStringParameterByName(name)
      return parameter !== undefined ? Number(parameter) : undefined
    },
    getStringParameterByName(name: string): string | undefined {
      const regex = new RegExp("[?&]" + name + "(=([^&#]*)|&|#|$)")
      const results = regex.exec(window.location.href)

      if (!results || !results[2])
        return undefined
      
      return decodeURIComponent(results[2].replace(/\+/g, " "));
    },
    onCustomerSelected(customer: Customer): void {
      this.customer = customer
    },
    onDeleteClicked(id: number): void {
      this.orderService.deleteOrder(id).then(() => {
        if ((this.orders.length - 1) === 0 && this.pageNumber > 1)
          this.onPageChanged(this.pageNumber - 1)
        else
          this.searchOrders()

        this.toast.success('Order deleted successfully.')
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to delete the order. Please try again.')
        }
      })
    },
    onPageChanged(pageNumber: number): void {
      this.pageNumber = pageNumber
      this.searchOrders()
    },
    onPageSizeChanged(pageSize: number): void {
      this.pageSize = pageSize
      this.search()
    },
    onTableRowClicked(id: number): void {
      this.$router.push('/order/' + id)
    },
    parseQueryStringAndSearch(): void {
      const ascendingFromUrl = this.getBooleanParameterByName("a")
      this.ascending = ascendingFromUrl !== undefined ? ascendingFromUrl : this.ascending

      const buildTypeIdFromUrl = this.getNumericParameterByName("bt")
      this.buildTypeId = buildTypeIdFromUrl !== undefined ? buildTypeIdFromUrl : this.buildTypeId

      const cartridgeIdFromUrl = this.getNumericParameterByName("ca")
      this.cartridgeId = cartridgeIdFromUrl !== undefined ? cartridgeIdFromUrl : this.cartridgeId

      const customerIdFromUrl = this.getNumericParameterByName("ci")
      const customerNameFromUrl = this.getStringParameterByName("cn")
      if (customerIdFromUrl !== undefined && customerNameFromUrl !== undefined)
        this.customer = new Customer(customerIdFromUrl, customerNameFromUrl.split(' ')[0], customerNameFromUrl.split(' ')[1], '', '', '', '', '', undefined, '')

      const isScheduledFromUrl = this.getBooleanParameterByName("s")
      this.isScheduled = isScheduledFromUrl !== undefined ? isScheduledFromUrl : this.isScheduled

      const onHoldFromUrl = this.getBooleanParameterByName("h")
      this.onHold = onHoldFromUrl !== undefined ? onHoldFromUrl : this.onHold

      const showCompletedOrdersFromUrl = this.getBooleanParameterByName("c")
      this.showCompletedOrders = showCompletedOrdersFromUrl !== undefined ? showCompletedOrdersFromUrl : this.showCompletedOrders

      const pageNumberFromUrl = this.getNumericParameterByName("pn")
      this.pageNumber = pageNumberFromUrl !== undefined ? pageNumberFromUrl : this.pageNumber

      const pageSizeFromUrl = this.getNumericParameterByName("ps")
      this.pageSize = pageSizeFromUrl !== undefined ? pageSizeFromUrl : this.pageSize

      const searchTermFromUrl = this.getStringParameterByName("st")
      this.searchTerm = searchTermFromUrl !== undefined ? searchTermFromUrl : this.searchTerm

      const sortFieldFromUrl = this.getStringParameterByName("sf")
      this.sortField = sortFieldFromUrl !== undefined ? sortFieldFromUrl : this.sortField

      this.searchOrders()
    },
    search(): void {
      this.pageNumber = 1
      this.searchOrders()
    },
    searchOrders(): void {
      this.loading = true
      this.updateUrl()
      this.orderService.searchOrders(this.searchTerm, this.pageNumber, this.pageSize, this.sortField, this.buildTypeId, this.cartridgeId, this.customer?.id, this.ascending, this.onHold, this.isScheduled, this.showCompletedOrders).then(pagingResponse => {
        this.totalResults = pagingResponse.totalResults
        this.orders = pagingResponse.results
        this.loading = false
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to load the orders. Try reloading the page.')
        }
      })
    },
    sortBy(sortField: string): void {
      if (this.sortField === sortField)
        this.ascending = !this.ascending
      else
        this.sortField = sortField

      this.searchOrders()
    },
    updateUrl(): void {
      let newQueryString = `?a=${this.ascending}&pn=${this.pageNumber}&ps=${this.pageSize}&sf=${this.sortField}&h=${this.onHold}&s=${this.isScheduled}&c=${this.showCompletedOrders}`

      if (this.buildTypeId !== undefined)
        newQueryString = newQueryString + `&bt=${this.buildTypeId}`

      if (this.cartridgeId !== undefined)
        newQueryString = newQueryString + `&ca=${this.cartridgeId}`

      if (this.customer !== undefined) {
        newQueryString = newQueryString + `&ci=${this.customer.id}`
        newQueryString = newQueryString + `&cn=${encodeURIComponent(this.customerName)}`
      }

      if (this.searchTerm !== '')
        newQueryString = newQueryString + `&st=${encodeURIComponent(this.searchTerm)}`
      
      this.$router.replace('/orders' + newQueryString)
    }
  }
})
